<template lang="pug">
.insights-activity-widget.insights-widget
  .widget-wrapper
    .widget-common.widget-small.widget-full-height
      .widget-title
        .d-flex.align-items-baseline
          | {{ title }}
          i.hi.hi-info-filled.ml-5(
            v-if="titleTooltip"
            v-tooltip="tooltipOptions"
            :title="titleTooltip"
          )

      .widget-body(:class="bodyClasses")
        template(v-if="isReady")
          .activity-information.d-flex.justify-content-between(v-if="!isEmptyWidget")
            .d-flex.flex-column.justify-content-between
              .score
                .percentage {{ score }}%
                .score-label Average
              AppChartLegend(
                :values="chartRanges"
                :include-values="false"
                orientation="vertical"
              )
            .chart.d-flex.align-items-end(v-if="chart")
              .legend.d-flex.flex-column-reverse.justify-content-between.align-items-end
                .value(
                  v-for="(value, key) in legendValues"
                  :key="key"
                )
                  span {{ value }}%
              .meter
                .section(
                  v-for="(item, key) in chartRanges"
                  :key="key"
                  v-tooltip="tooltipOptions"
                  :class="`activity-${item.key}`"
                  :title="item.tooltip"
                )
                .bar(:style="setBarHeight(score)")
          InsightsNoData(
            v-else
            :type="emptyReason"
          )
      .widget-footer-bordered(v-if="showWidgetBenchmarks")
        InsightsBenchmarksAverages(
          :averages="benchmarksAverages"
          :pins="benchmarkPins"
          :is-loading="!isReady"
          :widget-name="title"
        )
</template>

<script>
import { formatDuration } from '~/utils/time_utils';
import { INSIGHTS } from '~/app_constants';
import { get } from 'lodash-es';
import { Insights, InsightsTracking } from '~/mixins';
import { mapState, mapGetters } from 'vuex';

import AppChartLegend from '~/components/AppChartLegend.vue';

const titleTooltip = `Average mouse & keyboard activity by range`;

export default {
  name: 'InsightsActivityWidget',
  components: {
    AppChartLegend,
    InsightsBenchmarksAverages: () => import('~/components/insights/InsightsBenchmarksAverages.vue'),
    InsightsNoData: () => import('~/components/insights/InsightsNoData.vue'),
  },
  mixins: [Insights, InsightsTracking],
  data: () => ({
    widgetKey: INSIGHTS.api.highlights.activity,
    titleTooltip,
    tooltipOptions: Object.freeze({
      placement: 'top',
      html: true,
      template:
        '<div class="vue-tooltip top insights-activity-tooltip"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>',
    }),
    ranges: INSIGHTS.acitivity_ranges,
    legendValues: [0, 25, 50, 75, 100],
    title: 'Activity',
  }),
  computed: {
    ...mapState({
      widgetData: (state) => get(state.widgets, INSIGHTS.api.highlights.activity, null),
    }),
    ...mapGetters(['showWidgetBenchmarks', 'remoteVsOffice']),
    hasRemoteData() {
      return this.widgetData?.score_remote != null;
    },
    hasOfficeData() {
      return this.widgetData?.score_on_site != null;
    },
    benchmarksAverages() {
      if (this.isEmptyWidget) {
        return {};
      }

      if (!this.remoteVsOffice) {
        return this.widgetData?.benchmarks || {};
      }

      const all = this.widgetData?.benchmarks || {};
      const result = {};

      if (this.hasRemoteData) {
        result.remote = all.remote;
      }
      if (this.hasOfficeData) {
        result.on_site = all.on_site;
      }

      return result;
    },
    score() {
      return Math.round(this.widgetData?.score || 0);
    },
    benchmarkScores() {
      if (this.isEmptyWidget) {
        return {};
      }

      if (!this.remoteVsOffice) {
        return this.score;
      }

      const scores = {};

      if (this.hasRemoteData) {
        scores.remote = this.widgetData.score_remote;
      }
      if (this.hasOfficeData) {
        scores.on_site = this.widgetData.score_on_site;
      }

      return scores;
    },
    benchmarkPins() {
      const scores = this.benchmarkScores;

      if (typeof scores === 'object') {
        return Object.entries(scores).map(([key, value]) => ({ key, value }));
      }

      return [{ key: 'standard', value: scores }];
    },
    chart() {
      return this.widgetData?.chart
        ? Object.keys(this.widgetData.chart)
            .filter(
              (key) =>
                this.widgetData.chart[key] &&
                this.widgetData.chart[key].tracked > 0 &&
                this.widgetData.chart[key].value > 0,
            )
            .reduce(
              (acc, key) => ({
                ...acc,
                [key]: {
                  tracked: formatDuration(this.widgetData.chart[key].tracked, { showSeconds: 'never' }),
                  value: this.widgetData.chart[key].value * 100,
                },
              }),
              {},
            )
        : [];
    },
    chartRanges() {
      return Object.entries(this.ranges).map(([key, value]) => ({
        ...value,
        tooltip: this.chart[key] ? `${this.buildTooltip(this.chart[key])} ${value.tooltip}` : '',
      }));
    },
  },

  methods: {
    setBarHeight(value) {
      const height = value > 100 ? 100 : value;

      return `height: ${height}%`;
    },
    buildTooltip(item) {
      const [hours, minutes] = item.tracked.split(':');

      return `${hours}h ${minutes}m spent in`;
    },
  },
};
</script>
